import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, db } from '../firebase';
import {
  collection,
  serverTimestamp,
  doc,
  getDocs,
  query,
  where,
  setDoc,
  deleteDoc,
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { createFFmpeg, fetchFile } from '@ffmpeg/ffmpeg';

const ffmpeg = createFFmpeg({
  log: false,
  corePath: '/ffmpeg/ffmpeg-core.js', // Firebase Hosting の public/ffmpeg に配置されている想定
});

// ✅ 動画圧縮処理
export const compressVideo = async (file, onProgress) => {
  const threshold = 20 * 1024 * 1024;
  if (file.size <= threshold) {
    onProgress?.(0);
    return file;
  }

  if (!ffmpeg.isLoaded()) await ffmpeg.load();
  ffmpeg.setProgress(({ ratio }) => onProgress?.(ratio * 100));

  const inputName = 'in.mp4';
  const outputName = 'out.mp4';
  ffmpeg.FS('writeFile', inputName, await fetchFile(file));

  try {
    await ffmpeg.run(
      '-i', inputName,
      '-vcodec', 'libx264',
      '-b:v', '700k',
      '-preset', 'fast',
      outputName
    );
  } catch (err) {
    console.error('[FFmpeg] 圧縮失敗:', err);
    throw new Error('動画の圧縮に失敗しました');
  }

  const data = ffmpeg.FS('readFile', outputName);
  const blob = new Blob([data.buffer], { type: 'video/mp4' });

  return new File(
    [blob],
    file.name.replace(/\.[^/.]+$/, '') + '-compressed.mp4',
    { type: 'video/mp4' }
  );
};

// ✅ ファイル名整形
export const sanitizeFileName = (input) =>
  input.trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-_]/g, '');

// ✅ 年月フォルダ
const getSubfolderName = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}`;
};

// ✅ 重複チェック用ハッシュ
export const computeFileHash = async (file) => {
  const chunkSize = 1024 * 1024;
  const head = await file.slice(0, chunkSize).arrayBuffer();
  const tail = file.size > chunkSize
    ? await file.slice(file.size - chunkSize).arrayBuffer()
    : head;

  const combined = new Uint8Array(head.byteLength + tail.byteLength);
  combined.set(new Uint8Array(head), 0);
  combined.set(new Uint8Array(tail), head.byteLength);

  const hash = await crypto.subtle.digest('SHA-256', combined.buffer);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
};

// ✅ サムネイル生成（0.2秒時点 or 中央）＋ onseeked フォールバック対応
export const generateThumbnailBlob = (file, maxW = 320, maxH = 240, sec = 0.2) =>
  new Promise((resolve, reject) => {
    const vid = document.createElement('video');
    vid.src = URL.createObjectURL(file);
    vid.crossOrigin = 'anonymous';
    vid.muted = true;
    vid.playsInline = true;

    const handleCapture = () => {
      const ratio = Math.min(maxW / vid.videoWidth, maxH / vid.videoHeight);
      const cw = vid.videoWidth * ratio;
      const ch = vid.videoHeight * ratio;

      const canvas = document.createElement('canvas');
      canvas.width = cw;
      canvas.height = ch;
      canvas.getContext('2d').drawImage(vid, 0, 0, cw, ch);

      canvas.toBlob(
        (b) => b ? resolve(b) : reject(new Error('Blob生成失敗')),
        'image/png'
      );

      URL.revokeObjectURL(vid.src);
    };

    vid.onloadedmetadata = () => {
      vid.currentTime = Math.min(sec, vid.duration / 2);
    };

    vid.onseeked = handleCapture;
    vid.oncanplay = handleCapture;

    vid.onerror = () => reject(new Error('動画読み込みエラー'));
  });

// ✅ クリーンアップ処理（StorageとFirestore）
const cleanup = async (refs = [], docRef = null) => {
  for (const refItem of refs) {
    try {
      if (refItem) await deleteObject(refItem);
    } catch (e) {
      console.warn('クリーンアップ失敗（Storage）:', e);
    }
  }

  if (docRef) {
    try {
      await deleteDoc(docRef);
    } catch (e) {
      console.warn('クリーンアップ失敗（Firestore）:', e);
    }
  }
};

// ✅ Firebaseへアップロード
export const uploadFileToFirebase = async (
  file,
  path,
  baseName,
  onProgress,
  options = {}
) => {
  if (!file || !file.name || !file.type.startsWith('video/')) {
    throw new Error('無効なファイルです');
  }

  if (file.size > 200 * 1024 * 1024) {
    throw new Error('ファイルが大きすぎます（200MB まで）');
  }

  if (!options.firestoreCollection) {
    throw new Error('Firestore コレクション名が必要です');
  }

  const hash = await computeFileHash(file);
  const exists = !(await getDocs(query(collection(db, options.firestoreCollection), where('hash', '==', hash)))).empty;
  if (exists) throw new Error('この動画は既にアップロード済みです');

  const compressCb = (p) => onProgress?.(p * 0.2);
  const processedFile = await compressVideo(file, compressCb);

  const folder = getSubfolderName();
  const ext = processedFile.name.split('.').pop().toLowerCase();
  const finalName = `${sanitizeFileName(baseName)}-${uuidv4().slice(0, 8)}.${ext}`;
  const fullPath = `${path}/${folder}/${finalName}`;

  const commonMeta = {
    title: (options.title || baseName).slice(0, 100),
    hash,
    size: processedFile.size,
    ...options.metadata,
  };

  let fileRef;
  let thumbRef;
  const maxRetries = 3;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      fileRef = ref(storage, fullPath);
      const task = uploadBytesResumable(fileRef, processedFile);

      await new Promise((resolve, reject) => {
        task.on('state_changed',
          (snap) => onProgress?.(20 + (snap.bytesTransferred / snap.totalBytes) * 60),
          reject,
          resolve
        );
      });

      break;
    } catch (err) {
      if (attempt === maxRetries) throw err;
      await new Promise(r => setTimeout(r, 2 ** attempt * 1000));
    }
  }

  const fileUrl = await getDownloadURL(fileRef);

  const { duration, width, height } = await new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.src = URL.createObjectURL(processedFile);

    v.onloadedmetadata = () => {
      resolve({ duration: v.duration, width: v.videoWidth, height: v.videoHeight });
      URL.revokeObjectURL(v.src);
    };

    v.onerror = () => resolve({ duration: null, width: null, height: null });
  });

  const docRef = doc(collection(db, options.firestoreCollection));
  const docId = docRef.id;

  const thumbBlob = await generateThumbnailBlob(processedFile);
  const thumbName = `${sanitizeFileName(baseName)}-thumb-${docId}.png`;
  const thumbPath = `${path}/${folder}/${thumbName}`;
  thumbRef = ref(storage, thumbPath);

  await uploadBytesResumable(thumbRef, thumbBlob);
  const thumbUrl = await getDownloadURL(thumbRef);

  const metadata = {
    ...commonMeta,
    fileName: finalName,
    fileUrl,
    thumbUrl,
    uploadedAt: serverTimestamp(),
    duration,
    width,
    height,
  };

  try {
    await setDoc(docRef, metadata);
  } catch (err) {
    await cleanup([fileRef, thumbRef], docRef);
    throw new Error('Firestore 書き込みに失敗しました');
  }

  return { url: fileUrl, thumbUrl, id: docId };
};
